import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import CreateAccount from '../auth/createAccount'
import LoginAccount from '../auth/loginAccount'
import { CDN_BASE_URL } from '../../constants'
import ForgotPassword from '../auth/forgotPassword'
import Logo from '../../assets/Logo.svg'
import { useLocation } from 'react-router-dom'
import { useGrowthBookValues } from '../../contexts/GrowthBookContext'
import { trackRegistrationModalOpened } from '../../utils/analytics'
import { readLpSession } from '../../utils/session'

const AuthModal = ({
  isOpen,
  selectedStyle,
  hideBackgroundVideo = false,
  onSuccessContinue = null,
}) => {
  // Returning visitor whose stored session has NO spendable token — the redirect
  // arm (the platform consumed it on arrival) or a resumed checkout that 401'd.
  // The account exists, so open on the login card with the email prefilled rather
  // than a signup form the backend can only reject. A session WITH a token stays
  // on signup: createAccount reopens the paywall popup with it (utils/session.js).
  // Password only — Google and magic-link users have no password to log in with;
  // their recovery lives on the signup card (Google button / email-only → OTP).
  const [authView, setAuthView] = useState(() => {
    // Never on a Google OAuth return: createAccount owns that path, and it only
    // consumes the `?auth_token` while it is mounted — opening the login card
    // instead would strand the token in the URL.
    if (
      typeof window !== 'undefined' &&
      new URLSearchParams(window.location.search).has('auth_token')
    ) {
      return 'signup'
    }
    const stored = readLpSession()
    return stored && !stored.authToken && stored.source === 'password'
      ? 'login'
      : 'signup'
  })
  const location = useLocation()
  const { t } = useTranslation()
  const { magicLink } = useGrowthBookValues()
  const isMagicLink = magicLink === 'ON'

  const isHalloween = location.pathname.includes('/halloween')
  const isQuiz = location.pathname.includes('/quiz')

  // Fire `lander_registration_modal` whenever the registration form opens,
  // from any entry point (Home /signup, /quiz after style pick, /quiz-sfw on entry).
  useEffect(() => {
    if (isOpen) trackRegistrationModalOpened()
  }, [isOpen])

  const getAuthVideoAndPoster = () => {
    const videoMap = {
      Realistic: [
        {
          video: `${CDN_BASE_URL}/Realistic/auth_realistic.mp4`,
          poster: `${CDN_BASE_URL}/Realistic/first_frame_realistic.jpg`,
        },
        {
          video: `${CDN_BASE_URL}/Realistic/auth_realistic_2.mp4`,
          poster: `${CDN_BASE_URL}/Realistic/first_frame_realistic_2.jpg`,
        },
      ],
      Anime: [
        {
          video: `${CDN_BASE_URL}/Anime/auth_anime.mp4`,
          poster: `${CDN_BASE_URL}/Anime/first_frame_anime.jpg`,
        },
        {
          video: `${CDN_BASE_URL}/Anime/auth_anime_2.mp4`,
          poster: `${CDN_BASE_URL}/Anime/first_frame_anime_2.jpg`,
        },
      ],
    }

    const style = selectedStyle || 'Realistic'
    const videos = videoMap[style] || videoMap.Realistic

    return videos[Math.floor(Math.random() * videos.length)]
  }

  if (!isOpen) return null

  const overlayClassName = `auth-modal-overlay${hideBackgroundVideo ? ' auth-modal-overlay-no-bg' : ''}${isMagicLink ? ' magic-link' : ''}`
  const modalClassName = `auth-modal ${isHalloween ? 'auth-modal-halloween' : ''}${hideBackgroundVideo ? ' auth-modal-no-bg' : ''}`

  return (
    <div className={overlayClassName}>
      {!hideBackgroundVideo && (
        <div className='video-bg'>
          <video
            src={getAuthVideoAndPoster().video}
            poster={getAuthVideoAndPoster().poster}
            autoPlay
            muted
            loop
            playsInline
            alt='Authentication'
            className='auth-image'
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        </div>
      )}
      <div className='logo'>
        <img src={Logo} alt='' />
      </div>
      <div className={modalClassName}>
        <div className='auth-modal-content'>
          {/* Left Side - Auth Form */}
          <div className='auth-form-container'>
            {isMagicLink ? (
              <CreateAccount onSuccessContinue={onSuccessContinue} />
            ) : (
              <>
                {authView === 'login' && (
                  <LoginAccount
                    onSwitchToSignup={() => setAuthView('signup')}
                    onSwitchToForgot={() => setAuthView('forgot')}
                    onSuccessContinue={onSuccessContinue}
                  />
                )}
                {authView === 'signup' && (
                  <CreateAccount
                    onContinue={() => setAuthView('login')}
                    onSuccessContinue={onSuccessContinue}
                  />
                )}
                {authView === 'forgot' && <ForgotPassword onBackToLogin={() => setAuthView('login')} />}
              </>
            )}
          </div>

          {/* Right Side - Image */}
          {!hideBackgroundVideo && (
            <div className='auth-image-container'>
              <video
                src={getAuthVideoAndPoster().video}
                poster={getAuthVideoAndPoster().poster}
                autoPlay
                muted
                loop
                playsInline
                alt='Authentication'
                className='auth-image'
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            </div>
          )}
        </div>
        {isQuiz && (
          <div className='quiz-auth-disclaimer'>
            {t('auth_modal_disclaimer')}
          </div>
        )}
      </div>
    </div>
  )
}

AuthModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  selectedStyle: PropTypes.string,
  hideBackgroundVideo: PropTypes.bool,
  onSuccessContinue: PropTypes.func,
}

export default AuthModal
